import { Box } from "@bcc-ui/ui-components";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import style from "./software-packages.scss";
import ErrorMessage from "components/error-message/ErrorMessage";
import CircleInfo from "components/icons/CircleInfo";
import Download from "components/icons/Download";
import { BLANCCO_TOKEN_ID, fetchDynamicTierAddOns } from "components/licenses/common";
import StaticTable from "components/support/api-guide/StaticTable";
import { deduceTierLicenses } from "components/tenants/add-tenant/AddTenantForm";
import Tooltip from "components/tooltip/Tooltip";
import { LicenseData } from "domain/licenses";
import { SoftwarePackage, SoftwarePackagesData } from "domain/softwarePackages";
import { LicensingModel } from "domain/tenants";
import { LicenseResponse } from "services/licenses/LicenseService";
import { getCurrentTenantDetails, hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import formStyle from "styles/form.scss";
import { logger } from "utils/logging";
import { showShaChecksumToast } from "utils/shaChecksumUtils";

import testIds from "testIds.json";

interface Props {
    licensePromise: Promise<LicenseResponse>;
    fetchSoftwarePackagesData: () => Promise<SoftwarePackagesData>;
}

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

export const filterSoftwarePackages = (
    softwarePackages: SoftwarePackage[],
    licensesData: LicenseData[]
): SoftwarePackage[] => {
    if (userSessionService.currentUserDetails()?.tenantType === "INTERNAL" && !hasTenantCookie()) {
        return softwarePackages;
    }
    const filteredPackages: Set<SoftwarePackage> = new Set();
    const tenantDetails = getCurrentTenantDetails();
    if (tenantDetails.licensingModel == LicensingModel.BUNDLE_WITH_TOKEN) {
        const availableLicenses: string[] = [];
        licensesData.forEach((license) => {
            if (license.available <= 0 && license.expirationDate < new Date().toJSON()) {
                return;
            }
            if (license.type == BLANCCO_TOKEN_ID) {
                const deducedTierLicenses = deduceTierLicenses(tenantDetails.tenantTier, true);
                if (deducedTierLicenses) {
                    availableLicenses.push(...deducedTierLicenses);
                }
            } else {
                if (fetchDynamicTierAddOns(tenantDetails.tenantTier).includes(license.type)) {
                    availableLicenses.push(license.type);
                }
            }
        });
        softwarePackages.forEach((product) => {
            availableLicenses.forEach((each) => {
                if (
                    product.licenseIds.includes(each) &&
                    (typeof product.bundles === "undefined" ||
                        (product.bundles && product.bundles.includes(tenantDetails.tenantTier)))
                ) {
                    filteredPackages.add(product);
                }
            });
        });
    } else {
        softwarePackages.forEach((product) => {
            licensesData.forEach((license) => {
                if (product.licenseIds.includes(license.type)) {
                    filteredPackages.add(product);
                }
            });
        });
    }
    return Array.from(filteredPackages);
};

const connector = connect(mapState);

const SoftwarePackages = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [cellData, setCellData] = React.useState<Array<Array<string | JSX.Element | null>>>([[null]]);
    const data: string[] = [];
    const versionChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>, index: number) => {
        data[index] = event.target.value;
    };
    const [errorMessage, showErrorMessage] = React.useState<boolean>(false);

    const generateVersionSelect = (versions: SoftwarePackage["versions"], index: number): JSX.Element => {
        if (versions.length === 0) {
            return <div>{t("Common.na")}</div>;
        }
        return versions.length > 1 ? (
            <select
                onChange={(event) => versionChangeHandler(event, index)}
                onMouseDown={(event) => event.stopPropagation()}
                className={classNames(formStyle.select, style.versionsDropdown)}
                key={index}
                data-testid={testIds.workArea.support.supportAndHelp.cards.software.table.versionSelect}
            >
                {versions.map((data, key) => (
                    <option key={key} value={data.downloadUrl}>
                        {data.version}
                    </option>
                ))}
            </select>
        ) : (
            <div data-testid={testIds.workArea.support.supportAndHelp.cards.software.table.versionSelect}>
                {versions[0].version}
            </div>
        );
    };

    const downloadSoftwarePackage = (index: number): void => {
        window.location.href = data[index];
    };

    const sortSoftwarePackages = (products: SoftwarePackage[]): void => {
        // Lexicographic sort
        products.sort((a, b) => a.productName.localeCompare(b.productName));
        // Versions sorting
        products.forEach((softwarePackage) => {
            softwarePackage.versions.sort((a, b) => b.version.localeCompare(a.version, undefined, { numeric: true }));
        });
    };

    const showErrorDialog = (e: Error) => {
        logger.error(e);
        setLoading(false);
        showErrorMessage(true);
    };

    const checkForEmptyShaKey = (product: SoftwarePackage, index: number) => {
        const matchingDownloadUrl = product.versions.find((item) => item.downloadUrl === data[index]);
        return matchingDownloadUrl?.sha256 === "";
    };

    const toastForShaKeys = (product: SoftwarePackage, index: number) => {
        const isEmptyShaKey = checkForEmptyShaKey(product, index);

        if (isEmptyShaKey) {
            return;
        }

        const matchingVersion = product.versions.find((item) => item.downloadUrl === data[index]);
        const shaValue = matchingVersion?.sha256;

        if (!shaValue) {
            return null;
        }

        return (
            <div
                onClick={() => {
                    showShaChecksumToast(
                        shaValue,
                        product.productName,
                        props.theme.contentBackgroundColor,
                        t,
                        props.theme.iconFillColor,
                        props.theme.whiteColor
                    );
                }}
                className={style.infoIcon}
            >
                <Tooltip placement="top" content={t("Support.supportAndHelp.softwarePackages.verifyDownloadFile")}>
                    <div>
                        <CircleInfo color={props.theme.iconFillColor} />
                    </div>
                </Tooltip>
            </div>
        );
    };

    React.useEffect(() => {
        try {
            setLoading(true);
            const softwarePackagesPromise = props.fetchSoftwarePackagesData();
            Promise.all([softwarePackagesPromise, props.licensePromise])
                .then((promises) => {
                    const softwarePackagesData: SoftwarePackagesData = promises[0];
                    const licenseData: LicenseData[] = promises[1].licenses;
                    const filteredPackages = filterSoftwarePackages(softwarePackagesData.productDownloads, licenseData);
                    sortSoftwarePackages(filteredPackages);
                    setCellData(
                        filteredPackages.map((product: SoftwarePackage, index: number) => {
                            if (product.versions.length > 0) {
                                data.push(product.versions[0].downloadUrl);
                            }

                            return [
                                <div
                                    key={index}
                                    data-testid={testIds.workArea.support.supportAndHelp.cards.software.table.nameLabel}
                                >
                                    {product.productName}
                                </div>,
                                generateVersionSelect(product.versions, index),
                                product.versions.length > 0 ? (
                                    <Box verticalAlignment="Center" horizontalAlignment="Left">
                                        <div
                                            onClick={() => downloadSoftwarePackage(index)}
                                            key={index}
                                            className={style.downloadIcon}
                                            data-testid={
                                                testIds.workArea.support.supportAndHelp.cards.software.table
                                                    .versionDownloadButton
                                            }
                                        >
                                            <Download color={props.theme.iconFillColor} />
                                        </div>
                                        <>{toastForShaKeys(product, index)}</>
                                    </Box>
                                ) : (
                                    ""
                                ),
                            ];
                        })
                    );
                    setLoading(false);
                })
                .catch((e: Error) => showErrorDialog(e))
                .finally(() => setLoading(false));
        } catch (e) {
            showErrorDialog(e);
        }
    }, []);
    return (
        <>
            <div className={style.heading}>{t("Support.supportAndHelp.softwarePackages.introduction")}</div>
            {errorMessage ? (
                <ErrorMessage />
            ) : (
                <>
                    <div className={style.tableContainer}>
                        <StaticTable
                            headers={[
                                {
                                    value: t("Support.supportAndHelp.softwarePackages.product"),
                                },
                                {
                                    value: t("Support.supportAndHelp.softwarePackages.version"),
                                },
                                {
                                    value: t("Common.download"),
                                },
                            ]}
                            cells={cellData}
                            loading={loading}
                            tableClass={style.table}
                            testId={testIds.workArea.support.supportAndHelp.cards.software.table.itself}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default connector(SoftwarePackages);
